import { useState } from "react";
import { formatInr } from "../utils/money";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { SquarePen } from "lucide-react";
import { updateServiceCharges } from "../apis";
import DeskDialog, { Field, inputClass } from "./DeskDialog";

/**
 * Setting the delivery charge by hand. A reason is always asked for. Above the
 * limit the business has set (or when none is set) a senior approver has to do
 * it, and core's refusal says so; it is shown in the dialog. "Back to the
 * city's charge" hands it back, which needs nobody.
 */
export default function EditServiceCharge({
  amount,
  override,
  disabled = false,
}: {
  amount: number;
  /** The hand-set charge, when there is one. */
  override?: number | null;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: updateServiceCharges,
    onSuccess: (_result, variables) => {
      toast.success(variables.amount === null ? "Delivery charge is back to the city's charge" : "Delivery charge updated");
      queryClient.invalidateQueries(["orderDetails"]);
      setOpen(false);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "The delivery charge could not be changed."),
  });

  const typed = value.trim() === "" ? null : Number(value);
  const amountOk = typed !== null && Number.isFinite(typed) && typed >= 0;
  const reasonOk = reason.trim().length >= 3;

  return (
    <>
      <button
        type="button"
        aria-label="Edit delivery charge"
        disabled={disabled}
        onClick={() => {
          setValue("");
          setReason("");
          setError(null);
          setOpen(true);
        }}
        className="cursor-pointer rounded p-1 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#165FE3] disabled:cursor-not-allowed disabled:opacity-40"
      >
        <SquarePen className="w-4 h-4 text-blue-700" />
      </button>
      <DeskDialog
        open={open}
        onOpenChange={setOpen}
        title="Edit delivery charge"
        description={override != null ? `Set by hand to ${formatInr(override)}.` : "The charge now on the order is the city's."}
        busy={mutation.isLoading}
        submitDisabled={!amountOk || !reasonOk}
        error={error}
        onSubmit={() => {
          setError(null);
          mutation.mutate({ amount: typed, reason });
        }}
      >
        <Field label="Delivery charge (INR)" htmlFor="sc-amount" hint={`Now ${formatInr(Number(amount || 0))}`}>
          <input
            id="sc-amount"
            className={inputClass}
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter the delivery charge"
          />
        </Field>
        <Field label="Reason" htmlFor="sc-reason" hint="At least 3 characters. It is kept with the change.">
          <textarea
            id="sc-reason"
            className={`${inputClass} min-h-[72px]`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is the charge being changed?"
          />
        </Field>
        {override != null ? (
          <button
            type="button"
            disabled={!reasonOk || mutation.isLoading}
            onClick={() => {
              setError(null);
              mutation.mutate({ amount: null, reason });
            }}
            className="w-fit cursor-pointer text-sm font-medium text-[#165FE3] underline underline-offset-2 hover:text-[#1942B8] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Back to the city's charge
          </button>
        ) : null}
      </DeskDialog>
    </>
  );
}
